import React from 'react';
/**
 * PieChartRenderer — Interactive donut/pie chart with selection.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s67.
 * Optimized: extracted shared data normalization + selection hook (~211r → ~130r).
 */

import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { QuestionRendererProps } from './types';
import { normalizeChartData, ChartDataPoint, CHART_COLORS } from './chartDataUtils';
import { useChartSelection } from './useChartSelection';
interface ChartEntry extends ChartDataPoint {
  name: string;
  originalIndex: number;
}
export function PieChartRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.data || {};
  const rawData = normalizeChartData(config);
  const title = config.title || '';
  const colors = config.colors as string[] || CHART_COLORS;
  const hideValues = config.hideValues !== false;
  const chartData: ChartEntry[] = rawData.map((d, index) => ({
    ...d,
    name: d.label,
    originalIndex: index
  }));
  const {
    selectedIndex,
    handleItemClick,
    handleKeyDown
  } = useChartSelection({
    questionId: question.id,
    itemCount: chartData.length,
    idPrefix: 'pie-option',
    onAnswer,
    onReadyChange,
    shouldValidate
  });
  const CustomTooltip = ({
    active,
    payload
  }: {
    active?: boolean;
    payload?: Array<{
      payload: ChartEntry;
    }>;
  }) => {
    if (!active || !payload?.[0]) return null;
    const item = payload[0].payload;
    return <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-slate-200 text-sm" data-id="element-7209">
        <span className="font-semibold text-slate-700" data-id="element-7210">{item.name}</span>
        {!hideValues && <span className="text-slate-500 ml-2" data-id="element-7211">{item.value}</span>}
      </div>;
  };
  return <div className="w-full max-w-3xl mx-auto flex flex-col items-center" data-id="element-7212">
      <div className="w-full h-[300px] md:h-[400px] bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-col" aria-hidden="true" data-id="element-7213">
        {title && <h3 className="text-center text-slate-700 font-semibold mb-2" data-id="element-7214">
            {title}
          </h3>}
        <div className="flex-1 w-full relative" data-id="element-7215">
          <ResponsiveContainer width="100%" height="100%" data-id="element-7216">
            <PieChart data-id="element-7217">
              <Pie data={chartData} cx="50%" cy="50%" innerRadius="40%" outerRadius="80%" paddingAngle={2} dataKey="value" onClick={handleItemClick} cursor="pointer" data-id="element-7218">
                {chartData.map((_: ChartEntry, index: number) => {
                const isSelected = selectedIndex === index;
                return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} stroke={isSelected ? '#1e1b4b' : '#ffffff'} strokeWidth={isSelected ? 3 : 1} className="transition-all duration-200" style={{
                  opacity: selectedIndex !== null && !isSelected ? 0.6 : 1,
                  filter: isSelected ? 'drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.2))' : 'none'
                }} data-id="element-7219" />;
              })}
              </Pie>
              <Tooltip content={<CustomTooltip data-id="element-7221" />} data-id="element-7220" />
              <Legend verticalAlign="bottom" height={36} iconType="circle" formatter={value => <span className="text-slate-600 text-sm" data-id="element-7223">{value}</span>} data-id="element-7222" />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div role="listbox" aria-label="Kies een deel" className="flex flex-wrap justify-center gap-2 mt-4" data-id="element-7224">
        {chartData.map((entry: ChartEntry, index: number) => <button key={`option-${index}`} id={`pie-option-${index}`} role="option" aria-selected={selectedIndex === index} tabIndex={0} onClick={() => handleItemClick(null, index)} onKeyDown={e => handleKeyDown(e, index)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${selectedIndex === index ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-7225">
            {entry.name}
          </button>)}
      </div>

      <p className="text-center text-slate-500 text-sm mt-4" data-id="element-7226">
        Klik op een deel of gebruik de pijltjestoetsen
      </p>
    </div>;
}